import { ReservationRequiredField, ClientPod } from '../generated/graphql';
import { useClientRequiredFieldsAndPod } from '../hooks/useClientRequiredFieldsAndPod';
import { useValidDODID } from '../hooks/useValidDODID';
import {
  getGuestLevelFields,
  GUEST_FIELD_MAPPING,
} from '../utilities/guestFieldMapping';
import { required, composeValidators } from '../utilities/validators';

import RequiredFieldItem from './RequiredFieldItem';

interface GuestRequiredFieldsProps {
  className?: string;
}

export default function GuestRequiredFields({
  className = '',
}: GuestRequiredFieldsProps) {
  const { clientRequiredFields, clientPod } = useClientRequiredFieldsAndPod();
  const validDODID = useValidDODID();

  // Get only guest-specific fields
  const guestFields = getGuestLevelFields(
    clientRequiredFields.map((field) => field.value),
  );

  if (guestFields.length === 0) {
    return null;
  }

  // Fields that should be required for guests
  const requiredFields = [
    ReservationRequiredField.Rank,
    ReservationRequiredField.DodId,
  ];

  // Gender is only required for Government Solutions clients
  const isGovernmentSolutions = clientPod === ClientPod.GovernmentSolutions;
  if (isGovernmentSolutions) {
    requiredFields.push(ReservationRequiredField.Gender);
  }

  return (
    <div className={className}>
      {guestFields.map((fieldType) => {
        const fieldConfig = clientRequiredFields.find(
          (f) => f.value === fieldType,
        );
        if (!fieldConfig) return null;

        const isRequired = requiredFields.includes(fieldType);

        // Use the mapped property name for the form field
        const fieldName =
          GUEST_FIELD_MAPPING[fieldType as keyof typeof GUEST_FIELD_MAPPING] ||
          fieldType;

        // Determine validation based on field type and required status
        let validation;
        if (fieldType === ReservationRequiredField.DodId) {
          // DoD ID always needs format validation, compose with required if needed
          validation = isRequired
            ? composeValidators(required, validDODID)
            : validDODID;
        } else {
          // Other fields just use required validation if needed
          validation = isRequired ? required : undefined;
        }

        return (
          <RequiredFieldItem
            key={fieldType}
            name={fieldName}
            label={fieldConfig.label}
            fieldType={fieldType}
            required={isRequired}
            validation={validation}
          />
        );
      })}
    </div>
  );
}
